import { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import CreateEditLayout from '@features/workspace/components/layouts/sub/create-edit-layout';
import { INIT_DATA } from '@features/workspace/components/users/components/user-details-fields';
import { INIT_DATA as INIT_ADDRESS_DATA } from '@features/workspace/components/users/components/address-fields';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import history from '@app/services/history';
import LeaveFormSaveDialog from '@features/workspace/components/dialogs/leave-form-save-dialog';
import {
  selectIsUsersLoading,
  selectUsersData,
} from '@features/workspace/store/selectors';
import { updateUser } from '@features/workspace/store/actions';
import userSchema from '@features/workspace/components/users/schemas/user-schema';
import { setIsLoading } from '@store/features/loading';
import { setNotification } from '@store/features/notification';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import UserForm from '@features/workspace/components/users/form';
import { EmployeePositionEnum, IdentityTypeEnum } from '@utils/constants';
import EmailChangeDialog from '@features/workspace/views/company/components/dialogs/email-change-dialog';
import { UserManagementMode } from '@features/workspace/components/users/utils/constants';
import { Loading } from '@features/workspace/components/layouts/v2/loading';
import logger from '@app/services/logger';
import { IUserData } from '../../types';

const UserEdit: FC<any> = ({ type = UserManagementMode.Director, backUri }) => {
  const { user_id } = useParams();
  const dispatch = useAppDispatch();

  const usersData = useAppSelector(selectUsersData);

  const targetUser = useMemo(() => {
    return usersData?.find((user: IUserData) => user.id === user_id);
  }, [user_id, usersData]);

  const isUsersLoading = useAppSelector(selectIsUsersLoading);

  const [initData, setInitData] = useState(INIT_DATA);
  const [openLeaveFormSaveDialog, setOpenLeaveFormSaveDialog] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);

  const { register, handleSubmit, formState, reset, control, watch, setValue } =
    useForm({
      defaultValues: initData,
      resolver: yupResolver(userSchema),
    });

  const { errors, isDirty, dirtyFields } = formState;

  logger.debug('errors', errors);
  logger.debug('isDirty', isDirty);
  logger.debug('dirtyFields', dirtyFields);

  const observedFormData = watch();

  useEffect(() => {
    logger.debug('Form data changed');
  }, [observedFormData]);

  useEffect(() => {
    if (targetUser) {
      const initState = {
        ...INIT_DATA,
        ..._.omitBy(_.omit(targetUser, ['address']), _.isNil),
        address: {
          ...INIT_ADDRESS_DATA,
          ..._.omitBy(targetUser?.address, _.isNil),
        },
      };

      setInitData(initState);
      reset(initState);
    }
  }, [targetUser, reset]);

  useEffect(() => {
    if (
      observedFormData?.person?.identityDocuments?.[0]?.identityType ===
      IdentityTypeEnum.ForeignIdNumber
    ) {
      setValue('person.sisAbsentZAIDReason', null);
    }
  }, [observedFormData?.person?.identityDocuments?.[0]?.identityType]);

  const onSave = async () => {
    const formData = { ...observedFormData };

    logger.debug('onSave', formData);

    dispatch(setIsLoading(true));

    if (type === UserManagementMode.AuthPerson) {
      formData.position = EmployeePositionEnum.AuthPerson;
    }

    if (formData.person.dateOfBirth) {
      formData.person.dateOfBirth = moment.isMoment(formData.person.dateOfBirth)
        ? formData.person.dateOfBirth.format('YYYY-MM-DD')
        : moment(formData.person.dateOfBirth).format('YYYY-MM-DD');
    }

    try {
      await dispatch(
        updateUser({
          userId: formData?.id,
          data: formData,
        })
      ).unwrap();
      dispatch(
        setNotification({
          message: 'User successfully updated.',
          variant: 'success',
        })
      );
      goBack();
    } catch (err) {
      logger.error(err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const goBack = () => {
    history.push(backUri);
  };

  const onGoBack = () => {
    if (isDirty) {
      setOpenLeaveFormSaveDialog(true);
    } else {
      goBack();
    }
  };

  const pendingEmailKey = `${targetUser?.id}-email-change`;

  const pendingEmailData = JSON.parse(
    sessionStorage.getItem(pendingEmailKey) || 'null'
  );

  if (pendingEmailData && moment() > moment(pendingEmailData.expireAt)) {
    sessionStorage.removeItem(pendingEmailKey);
    pendingEmailData.email = null;
  }

  const pendingEmail = pendingEmailData ? pendingEmailData.email : null;

  const setPendingEmail = (email: string) => {
    if (email) {
      sessionStorage.setItem(
        pendingEmailKey,
        JSON.stringify({
          expireAt: moment().add(1, 'hour').format(),
          email,
        })
      );
    }
  };

  return (
    <CreateEditLayout
      backUri={backUri}
      errors={errors}
      onBack={onGoBack}
      onCancel={onGoBack}
      onSave={handleSubmit(onSave)}
    >
      {isUsersLoading && <Loading />}
      {!isUsersLoading && (
        <UserForm
          control={control}
          data={observedFormData}
          errors={errors}
          openChangeEmail={() => setChangeEmailOpen(true)}
          pendingEmail={pendingEmail}
          register={register}
          type={type}
        />
      )}
      <EmailChangeDialog
        open={changeEmailOpen}
        setPendingEmail={setPendingEmail}
        user={targetUser}
        onClose={() => setChangeEmailOpen(false)}
      />
      <LeaveFormSaveDialog
        open={openLeaveFormSaveDialog}
        onAccept={handleSubmit(onSave)}
        onClose={() => setOpenLeaveFormSaveDialog(false)}
        onReject={() => {
          goBack();
        }}
      />
    </CreateEditLayout>
  );
};

export default UserEdit;
